// exports Frame, default: Frame

import { map } from 'lodash';

import { Base, moveTo, lineTo } from 'gear';

import { Point }  from './point';
import { Bounds } from './bounds';

import { Xml } from '../util';

const Point_distance     = Point.distance,
      Point_distance2    = Point.distance2,
      Point_orientedSide = Point.orientedSide,
      Point_isLeft       = Point.isLeft,
      Point_isRight      = Point.isRight,
      Point_doSegmentsIntersect = Point.doSegmentsIntersect,
      _Math_min = Math.min,
      _Math_max = Math.max;

function toPoint( p ){
  return new Point(p.x,p.y);
}

export const Frame = function(p0,p1,p2,p3) {
  const _this = this;
  if( ! ( _this instanceof Frame )) {
    return new Frame(p0,p1,p2,p3);
  }
  if( p1 === undefined ) {
    const frame = p0;
    if( frame ) {
      _this.p0 = toPoint(frame.p0);
      _this.p1 = toPoint(frame.p1);
      _this.p2 = toPoint(frame.p2);
      _this.p3 = toPoint(frame.p3);
    }
    else {
      _this.p0 = new Point(0,0);
      _this.p1 = new Point(0,0);
      _this.p2 = new Point(0,0);
      _this.p3 = new Point(0,0);
    }
  }
  else {
    _this.p0 = toPoint(p0);
    _this.p1 = toPoint(p1);
    _this.p2 = toPoint(p2);
    _this.p3 = toPoint(p3);
  }
};
Base.Type['Frame'] = Frame;

export default Frame;

Frame.fromRect = function(x,y,width,height) {
  return new Frame( { p0: { x:x      , y:y },
                      p1: { x:x+width, y:y },
                      p2: { x:x+width, y:y+height },
                      p3: { x:x      , y:y+height } } );
};

const _toFixed = function(v) {
  return +v.toFixed(4);
};
const _pointToFixed = function(p) {
  return _toFixed(p.x) + ' ' + _toFixed(p.y);
};

const frame_textualize = function() {
  const _this = this, sep = ' ';
  return _pointToFixed(_this.p0) + sep +
         _pointToFixed(_this.p1) + sep +
         _pointToFixed(_this.p2) + sep +
         _pointToFixed(_this.p3) ;
};

const frameFromBounds = Frame.fromBounds = function(bounds) {
  return new Frame({ p0: { x: bounds.lx, y: bounds.ly }, p1: { x: bounds.hx, y: bounds.ly }, p2: { x: bounds.hx, y: bounds.hy }, p3: { x: bounds.lx, y: bounds.hy} });
};
Frame.around = function(center, width, height) {
  return frameFromBounds(Bounds.around(center,width,height));
};

Frame.ofPoints = function(points) {
  return frameFromBounds(Bounds.ofPoints(points));
} ;

Frame.prototype = {
  constructor: Frame,
  type: 'Frame',

  get bounds() {
     const _this = this,
         p0 = _this.p0,
         p1 = _this.p1,
         p2 = _this.p2,
         p3 = _this.p3;
     return new Bounds( _Math_min(p0.x,p1.x,p2.x,p3.x),  // lx
                           _Math_min(p0.y,p1.y,p2.y,p3.y),  // ly
                           _Math_max(p0.x,p1.x,p2.x,p3.x),  // hx
                           _Math_max(p0.y,p1.y,p2.y,p3.y)); // hy
  },
  get width() {
    return Point_distance(this.p0,this.p1);
  },
  get height() {
    return Point_distance(this.p0,this.p3);
  },

  get width2() {
    return Point_distance2(this.p0,this.p1);
  },
  get height2() {
    return Point_distance2(this.p0,this.p3);
  },

  get center() {
    return this.interpolate(0.5,0.5);
  },

  get dir_x() {
    return this.p1.minus(this.p0).normalize();
  },

  get dir_y() {
    return this.p3.minus(this.p0).normalize();
  },

  clone() {
    return new Frame(this);
  },
  isEqual( other ) {
    return this.p0.isEqual(other.p0) &&
           this.p1.isEqual(other.p1) &&
           this.p2.isEqual(other.p2) &&
           this.p3.isEqual(other.p3);
  },
  isNear( other,eps) {
    return this.p0.isNear(other.p0,eps) &&
           this.p1.isNear(other.p1,eps) &&
           this.p2.isNear(other.p2,eps) &&
           this.p3.isNear(other.p3,eps);
  },
  translate ( dx,dy) { if( dy === undefined ) { const o = dx; dx=o.x; dy=o.y; }
    const _this = this;
    _this.p0.x += dx; _this.p0.y += dy;
    _this.p1.x += dx; _this.p1.y += dy;
    _this.p2.x += dx; _this.p2.y += dy;
    _this.p3.x += dx; _this.p3.y += dy;
    return this;
  },
  translated( dx,dy) {
    return this.clone().translate(dx,dy);
  },

  transform( t ) {
    this.p0.transform(t);
    this.p1.transform(t);
    this.p2.transform(t);
    this.p3.transform(t);
    return this;
  },
  transformed( t ) {
    return this.clone().transform(t);
  },

  changed( t ) {
    return new Frame({ p0: t(this.p0), p1: t(this.p1), p2: t(this.p2), p3: t(this.p3) });
  },

  interpolate( ax,ay) { if( ay === undefined ) { const r=ax; ax=r.x; ay=r.y; }
    const p01 = this.p0.scaled(1-ax) .plus( this.p1.scaled(ax) ),
        p32 = this.p3.scaled(1-ax) .plus( this.p2.scaled(ax) );

    return p01.scaled(1-ay) .plus( p32.scaled(ay) );
  },
  deinterpolate( /*point*/) {
    return this.center; // [TODO]
  },
  toXmlString: frame_textualize,
  textualize: frame_textualize,
  // Contains returns true iff the query point is inside the frame. If the
  // query point is on an edge or vertex of the frame, it is considered to
  // be inside.
  // TODO: this function always return true when the four frame vertices
  // and the query point are aligned. This should be corrected, although in
  // practice this case never happens.
  contains( p ) {
    const _this = this;
    switch(Point_orientedSide(_this.p0,_this.p1,p)){
      case 1: // left turn
        return(!(Point_isRight(_this.p1,_this.p2,p) ||
                 Point_isRight(_this.p2,_this.p3,p) ||
                 Point_isRight(_this.p3,_this.p0,p)));
      case -1: // right turn
        return(!(Point_isLeft(_this.p1,_this.p2,p) ||
                 Point_isLeft(_this.p2,_this.p3,p) ||
                 Point_isLeft(_this.p3,_this.p0,p)));
      default: // are aligned (or there is an error)
        return((!(Point_isRight(_this.p1,_this.p2,p) ||
                  Point_isRight(_this.p2,_this.p3,p) ||
                  Point_isRight(_this.p3,_this.p0,p)))
               ||
               (!(Point_isLeft(_this.p1,_this.p2,p) ||
                  Point_isLeft(_this.p2,_this.p3,p) ||
                  Point_isLeft(_this.p3,_this.p0,p))));
    };
  },
  extend(olx, oly, ohx, ohy) {
    // [TODO] This could take into account the rotation
    const _this = this, p0 = _this.p0, p1 = _this.p1, p2 = _this.p2, p3 = _this.p3,
        p01 = p1.minus(p0).normalize(),
        p12 = p2.minus(p1).normalize(),
        p23 = p3.minus(p2).normalize(),
        p30 = p0.minus(p3).normalize();

    p0.substract( p01.scaled(olx) );
    p0.add      ( p30.scaled(oly) );
    p1.add      ( p01.scaled(ohx) );
    p1.substract( p12.scaled(oly) );
    p2.substract( p23.scaled(ohx) );
    p2.add      ( p12.scaled(ohy) );
    p3.add      ( p23.scaled(olx) );
    p3.substract( p30.scaled(ohy) );
    return _this;
  },
  extended( olx,oly,ohx,ohy) { return this.clone().extend(olx,oly,ohx,ohy); },
  angle() {
    return this.p1.minus(this.p0) .angle();
  },
  draw( context, transform ) {
    context.beginPath();
    moveTo(context,this.p0,transform);
    lineTo(context,this.p1,transform);
    lineTo(context,this.p2,transform);
    lineTo(context,this.p3,transform);
    context.closePath();
  },

  setXmlAttr( node, tag ) {
    node.setAttribute(tag,this.toXmlString());
  }
};

Frame.translate = function(frame,tx,ty) {
  return frame.clone().translate(tx,ty);
};

Frame.transform = function(frame, t) {
  return frame.clone().transform(t);
};

Frame.fromXmlString = function(s) {
  const values = map(s.split(' '),parseFloat);
  return new Frame({
    p0: new Point(values[0], values[1]),
    p1: new Point(values[2], values[3]),
    p2: new Point(values[4], values[5]),
    p3: new Point(values[6], values[7])
  });
};

const _segmentIntersectsFrameEdges = function(p0,p1,frame) {
  return Point_doSegmentsIntersect(p0,p1,frame.p0,frame.p1) ||
         Point_doSegmentsIntersect(p0,p1,frame.p1,frame.p2) ||
         Point_doSegmentsIntersect(p0,p1,frame.p2,frame.p3) ||
         Point_doSegmentsIntersect(p0,p1,frame.p3,frame.p0);
};

const _oneCornerOfFrameInsideAnotherFrame = function(fa,fb) {
  return fb.contains(fa.p0) ||
         fb.contains(fa.p1) ||
         fb.contains(fa.p2) ||
         fb.contains(fa.p3);
};

Frame.areOverlapping = function(fa,fb) {
  //
  // Bounds-based rejection
  //
  const bfa = fa.bounds;
  const bfb = fb.bounds;

  if( bfa.lx > bfb.hx || bfb.lx > bfa.hx || bfa.ly > bfb.hy || bfb.ly > bfa.hy ) {
    return false ;
  }
  //
  // One corner inside or edges of fa against each edge of fb
  //
  if( _oneCornerOfFrameInsideAnotherFrame(fa,fb)   ||
      _oneCornerOfFrameInsideAnotherFrame(fb,fa)   ||
      _segmentIntersectsFrameEdges(fa.p0,fa.p1,fb) ||
      _segmentIntersectsFrameEdges(fa.p1,fa.p2,fb) ||
      _segmentIntersectsFrameEdges(fa.p2,fa.p3,fb) ||
      _segmentIntersectsFrameEdges(fa.p3,fa.p0,fb) ) {
    return true ;
  }
  return false ;
};

Frame.overlapPercentage = function(fa, fb) {
  // For now, we are just computing intersection of the bounding boxes.
  // It will be nice to have an algorithm to compute the real intersection of frames.
  const ba = fa.bounds;
  const bb = fb.bounds;
  return Bounds.overlapPercentage(ba, bb);
};

Frame.fromXmlAttr = function(node,tag,defaultValue) {
  const valueString = node.getAttribute(tag) ;
  if( valueString ) {
    return Frame.fromXmlString(valueString);
  }
  return defaultValue;
};

Xml.Parser['Frame'] = {
  decode( node, tag, v ) {
    return Frame.fromXmlAttr(node,tag,v);
  },
  encode( node, tag, v ) {
    v.setXmlAttr(node,tag);
  }
};



/** WEBPACK FOOTER **
 ** ./src/cdl/geometry/frame.js
 **/